import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import { CONTENT_KINDS, type ContentKind, type ContentPageSummary } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  type ColumnDef,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { slugify, STATUS_VARIANT } from '../lib/content.js';
import { useTableLabels } from '../lib/table-labels.js';

const ALL = 'all';

/**
 * Every page staff manage — policies, help articles, landing pages — with
 * where each one stands. Opening one goes to the full-page editor.
 */
export function ContentListPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kind, setKind] = useState<string>(ALL);
  const [creating, setCreating] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['content'],
    queryFn: () => api.contentAdminList(),
  });

  const rows = useMemo(
    () => (data ?? []).filter((page) => kind === ALL || page.kind === kind),
    [data, kind],
  );

  const columns = useMemo<ColumnDef<ContentPageSummary>[]>(
    () => [
      {
        accessorKey: 'title',
        header: t('content.column.page'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to={`/content/${encodeURIComponent(row.original.slug)}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium underline-offset-2 hover:underline"
            >
              {row.original.title}
            </Link>
            <p className="font-mono text-xs text-text-muted">{row.original.slug}</p>
          </div>
        ),
      },
      {
        accessorKey: 'kind',
        header: t('content.column.kind'),
        cell: ({ row }) => <Badge variant="outline">{t(`content.kind.${row.original.kind}`)}</Badge>,
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>
            {t(`content.status.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        accessorKey: 'publishedVersion',
        header: t('content.column.version'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.publishedVersion > 0 ? `v${row.original.publishedVersion}` : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: t('content.column.edited'),
        cell: ({ row }) => (
          <div className="text-sm text-text-muted">
            <p>{formatDateTimeIn(row.original.updatedAt, locale)}</p>
            {row.original.updatedByName ? <p className="text-xs">{row.original.updatedByName}</p> : null}
          </div>
        ),
      },
    ],
    [t, locale],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('content.title')}
        description={t('content.subtitle')}
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            {t('content.new')}
          </Button>
        }
      />

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value={ALL}>{t('content.kind.all')}</TabsTrigger>
          {CONTENT_KINDS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`content.kind.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DataTable
        labels={tableLabels}
        columns={columns}
        data={rows}
        isLoading={isPending}
        onRowClick={(row) => navigate(`/content/${encodeURIComponent(row.slug)}`)}
        emptyMessage={t('content.none')}
      />

      {creating ? (
        <NewPageDialog
          canCreateLegal={user?.role === 'OWNER'}
          onClose={() => setCreating(false)}
          onCreated={(slug) => navigate(`/content/${encodeURIComponent(slug)}`)}
        />
      ) : null}
    </div>
  );
}

function NewPageDialog({
  canCreateLegal,
  onClose,
  onCreated,
}: {
  canCreateLegal: boolean;
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ContentKind>('PAGE');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  // Follows the title until somebody types an address of their own.
  const [slugEdited, setSlugEdited] = useState(false);

  const create = useMutation({
    mutationFn: () => api.createContent({ kind, title: title.trim(), slug }),
    onSuccess: (page) => {
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      onCreated(page.slug);
    },
  });

  const valid = title.trim().length > 0 && slug.length >= 2;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('content.create.title')}</DialogTitle>
          <DialogDescription>{t('content.create.body')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label={t('content.column.kind')}>
            {() => (
              <Select value={kind} onValueChange={(value) => setKind(value as ContentKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_KINDS.filter((value) => value !== 'LEGAL' || canCreateLegal).map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`content.kind.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label={t('content.create.pageTitle')}>
            {(field) => (
              <Input
                {...field}
                autoFocus
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  if (!slugEdited) setSlug(slugify(event.target.value));
                }}
              />
            )}
          </FormField>
          <FormField label={t('content.create.slug')} hint={t('content.create.slugHint')}>
            {(field) => (
              <Input
                {...field}
                value={slug}
                className="font-mono"
                onChange={(event) => {
                  setSlugEdited(true);
                  setSlug(slugify(event.target.value));
                }}
              />
            )}
          </FormField>
          {create.error ? (
            <Alert variant="danger">
              {create.error instanceof ZuruflexApiError ? create.error.message : t('common:error.unexpected')}
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button disabled={!valid} isLoading={create.isPending} onClick={() => create.mutate()}>
            {t('content.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
